import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Eye, EyeOff, Copy } from "lucide-react";
import { useData } from "../../context/DataContext";
import { formatCurrency } from "../../lib/format";

const blank = {
    name: "",
    startingCity: "Cochin",
    route: "",
    days: 3,
    nights: 2,
    km: 350,
    price: 8000,
    image: "",
    highlight: "",
    inclusions: ["AC cab with experienced driver"],
    exclusions: ["Hotel accommodation"],
    itinerary: [{ day: 1, title: "Day 1", details: "Pickup and drive." }],
    active: true,
};

const ManagePackages = () => {
    const { packages, addPackage, updatePackage, deletePackage, duplicatePackage } = useData();
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blank);
    const [filterCity, setFilterCity] = useState("All");

    const filtered = useMemo(() => {
        if (filterCity === "All") return packages;
        return packages.filter((p) => p.startingCity === filterCity);
    }, [packages, filterCity]);

    const open = (pkg = null) => {
        if (pkg) {
            const sedan = pkg.sedanPrice ?? pkg.price ?? 0;
            setEditing(pkg.id);
            setForm({
                ...pkg,
                sedanPrice: sedan,
                suvPrice: pkg.suvPrice ?? Math.round((sedan * 1.30) / 100) * 100,
                tempoPrice: pkg.tempoPrice ?? Math.round((sedan * 1.75) / 100) * 100,
            });
        } else {
            setEditing("new");
            setForm(blank);
        }
    };
    const close = () => {
        setEditing(null);
        setForm(blank);
    };
    const save = (e) => {
        e.preventDefault();
        if (!form.name || !form.route) return toast.error("Name and route are required.");
        const sedan = Number(form.sedanPrice) || 0;
        const payload = {
            ...form,
            nights: form.days - 1,
            sedanPrice: sedan,
            suvPrice: Number(form.suvPrice) || sedan,
            tempoPrice: Number(form.tempoPrice) || sedan,
            // keep legacy `price` synced with sedan for backward compat
            price: sedan,
        };
        if (editing === "new") {
            addPackage(payload);
            toast.success("Package added.");
        } else {
            updatePackage(editing, payload);
            toast.success("Package updated.");
        }
        close();
    };
    const remove = (id) => {
        if (window.confirm("Delete this package?")) {
            deletePackage(id);
            toast.success("Package deleted.");
        }
    };
    const duplicate = (id) => {
        duplicatePackage(id);
        toast.success("Package duplicated.");
    };
    const toggleActive = (pkg) => updatePackage(pkg.id, { active: !pkg.active });

    const updateInclusion = (i, v) => {
        const arr = [...form.inclusions];
        arr[i] = v;
        setForm({ ...form, inclusions: arr });
    };
    const updateExclusion = (i, v) => {
        const arr = [...form.exclusions];
        arr[i] = v;
        setForm({ ...form, exclusions: arr });
    };

    return (
        <div data-testid="admin-packages-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl text-ink">Manage Packages</h1>
                    <p className="text-ink-light text-sm mt-1">{packages.length} curated tour packages</p>
                </div>
                <button onClick={() => open()} className="px-4 py-2.5 rounded-xl bg-saffron-500 hover:bg-saffron-600 text-white font-semibold flex items-center gap-2 shadow-md" data-testid="admin-pkg-add">
                    <Plus className="w-4 h-4" /> Add package
                </button>
            </div>

            <div className="mt-5 flex items-center gap-2 flex-wrap">
                {["All", "Cochin", "Trivandrum", "Calicut"].map((c) => (
                    <button
                        key={c}
                        onClick={() => setFilterCity(c)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                            filterCity === c ? "bg-saffron-500 border-saffron-500 text-white" : "border-slate-200 text-ink-light hover:border-saffron-300"
                        }`}
                        data-testid={`admin-pkg-filter-${c}`}
                    >
                        {c}
                    </button>
                ))}
            </div>

            <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-xs uppercase tracking-wider text-ink-light">
                                <th className="px-4 py-3 font-semibold">Package</th>
                                <th className="px-4 py-3 font-semibold">From</th>
                                <th className="px-4 py-3 font-semibold">Duration</th>
                                <th className="px-4 py-3 font-semibold">KM</th>
                                <th className="px-4 py-3 font-semibold">Sedan / SUV / Tempo</th>
                                <th className="px-4 py-3 font-semibold">Status</th>
                                <th className="px-4 py-3 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((p) => (
                                <tr key={p.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 max-w-md">
                                        <div className="flex items-center gap-3">
                                            <img src={p.image} alt="" className="w-12 h-9 rounded-md object-cover bg-slate-100" />
                                            <div className="font-semibold text-ink line-clamp-1">{p.route}</div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-ink-light">{p.startingCity}</td>
                                    <td className="px-4 py-3 text-ink-light">{p.days}D / {p.nights}N</td>
                                    <td className="px-4 py-3 text-ink-light">{p.km}</td>
                                    <td className="px-4 py-3 text-xs">
                                        <div className="flex flex-col gap-0.5">
                                            <span><span className="text-ink-light">Sedan:</span> <span className="font-semibold text-saffron-600">{formatCurrency(p.sedanPrice ?? p.price)}</span></span>
                                            <span><span className="text-ink-light">SUV:</span> <span className="font-semibold text-saffron-600">{formatCurrency(p.suvPrice ?? Math.round(((p.sedanPrice ?? p.price) * 1.30) / 100) * 100)}</span></span>
                                            <span><span className="text-ink-light">Tempo:</span> <span className="font-semibold text-saffron-600">{formatCurrency(p.tempoPrice ?? Math.round(((p.sedanPrice ?? p.price) * 1.75) / 100) * 100)}</span></span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button onClick={() => toggleActive(p)} className={`text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${p.active ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-ink-light"}`}>
                                            {p.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                            {p.active ? "Live" : "Hidden"}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <button onClick={() => duplicate(p.id)} className="p-2 rounded-md hover:bg-slate-100 text-ink-light" title="Duplicate"><Copy className="w-4 h-4" /></button>
                                        <button onClick={() => open(p)} className="p-2 rounded-md hover:bg-slate-100 text-ink-light"><Edit3 className="w-4 h-4" /></button>
                                        <button onClick={() => remove(p.id)} className="p-2 rounded-md hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-4" onClick={close}>
                    <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-display text-2xl text-ink">{editing === "new" ? "Add package" : "Edit package"}</h2>
                            <button onClick={close} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <Field label="Package name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                                </div>
                                <div className="md:col-span-2">
                                    <Field label="Route (e.g., Cochin – Munnar – Cochin)" required value={form.route} onChange={(v) => setForm({ ...form, route: v })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Starting city</label>
                                    <select value={form.startingCity} onChange={(e) => setForm({ ...form, startingCity: e.target.value })} className="bare mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white">
                                        <option>Cochin</option>
                                        <option>Trivandrum</option>
                                        <option>Calicut</option>
                                    </select>
                                </div>
                                <Field label="Days" type="number" value={form.days} onChange={(v) => setForm({ ...form, days: Number(v) })} />
                                <Field label="KM" type="number" value={form.km} onChange={(v) => setForm({ ...form, km: Number(v) })} />
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Vehicle pricing (₹)</label>
                                    <div className="grid grid-cols-3 gap-2 mt-1">
                                        <Field label="Sedan" type="number" value={form.sedanPrice} onChange={(v) => setForm({ ...form, sedanPrice: Number(v), price: Number(v) })} />
                                        <Field label="SUV" type="number" value={form.suvPrice} onChange={(v) => setForm({ ...form, suvPrice: Number(v) })} />
                                        <Field label="Tempo Van" type="number" value={form.tempoPrice} onChange={(v) => setForm({ ...form, tempoPrice: Number(v) })} />
                                    </div>
                                    <div className="text-[11px] text-ink-light mt-1">Sedan is the displayed "From" price on public listings.</div>
                                </div>
                                <Field label="Image URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
                                <Field label="Highlight tag" value={form.highlight} onChange={(v) => setForm({ ...form, highlight: v })} />
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Inclusions</label>
                                {form.inclusions.map((inc, i) => (
                                    <div key={i} className="flex gap-2 mt-1.5">
                                        <input value={inc} onChange={(e) => updateInclusion(i, e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                                        <button type="button" onClick={() => setForm({ ...form, inclusions: form.inclusions.filter((_, k) => k !== i) })} className="px-2 text-red-500 text-xs">Remove</button>
                                    </div>
                                ))}
                                <button type="button" onClick={() => setForm({ ...form, inclusions: [...form.inclusions, ""] })} className="text-xs text-saffron-600 font-semibold mt-2">+ Add inclusion</button>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Exclusions</label>
                                {form.exclusions.map((exc, i) => (
                                    <div key={i} className="flex gap-2 mt-1.5">
                                        <input value={exc} onChange={(e) => updateExclusion(i, e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                                        <button type="button" onClick={() => setForm({ ...form, exclusions: form.exclusions.filter((_, k) => k !== i) })} className="px-2 text-red-500 text-xs">Remove</button>
                                    </div>
                                ))}
                                <button type="button" onClick={() => setForm({ ...form, exclusions: [...form.exclusions, ""] })} className="text-xs text-saffron-600 font-semibold mt-2">+ Add exclusion</button>
                            </div>

                            <label className="flex items-center gap-2 text-sm pt-2">
                                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-saffron-500 w-4 h-4" />
                                Show on public site
                            </label>

                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button type="button" onClick={close} className="px-4 py-2 rounded-lg border border-slate-200 text-ink font-semibold text-sm">Cancel</button>
                                <button type="submit" className="px-5 py-2 rounded-lg bg-saffron-500 hover:bg-saffron-600 text-white font-semibold text-sm">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, type = "text", value, onChange, required }) => (
    <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">{label}{required && " *"}</label>
        <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white" />
    </div>
);

export default ManagePackages;
